import React, { useState, useEffect } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { TaxConfigurationService } from '../../services/taxConfigurationService';
import TaxRatesTable from './components/TaxRatesTable';
import JurisdictionManager from './components/JurisdictionManager';
import ComplianceTracker from './components/ComplianceTracker';
import AITaxAssistant from './components/AITaxAssistant';
import TaxRateFormModal from './components/TaxRateFormModal';

const TaxConfiguration = () => {
  const { user, organization } = useAuth();
  const [activeTab, setActiveTab] = useState('tax-rates');
  const [taxRates, setTaxRates] = useState([]);
  const [jurisdictions, setJurisdictions] = useState([]);
  const [complianceRules, setComplianceRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showTaxRateModal, setShowTaxRateModal] = useState(false);
  const [selectedTaxRate, setSelectedTaxRate] = useState(null);

  useEffect(() => {
    if (organization?.id) {
      loadTaxConfigurationData();
    }
  }, [organization?.id]);

  const loadTaxConfigurationData = async () => {
    try {
      setLoading(true);
      setError('');

      const [ratesData, jurisdictionsData, complianceData] = await Promise.all([
        TaxConfigurationService?.getAllTaxRates(organization?.id),
        TaxConfigurationService?.getAllJurisdictions(organization?.id),
        TaxConfigurationService?.getAllComplianceRules(organization?.id)
      ]);

      setTaxRates(ratesData);
      setJurisdictions(jurisdictionsData);
      setComplianceRules(complianceData);
    } catch (err) {
      console.error('Error loading tax configuration:', err);
      setError(err?.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTaxRate = () => {
    setSelectedTaxRate(null);
    setShowTaxRateModal(true);
  };

  const handleEditTaxRate = (taxRate) => {
    setSelectedTaxRate(taxRate);
    setShowTaxRateModal(true);
  };

  const handleTaxRateSubmit = async (taxRateData) => {
    try {
      if (selectedTaxRate) {
        await TaxConfigurationService?.updateTaxRate(selectedTaxRate?.id, taxRateData);
      } else {
        await TaxConfigurationService?.createTaxRate({
          ...taxRateData,
          organizationId: organization?.id
        });
      }
      
      setShowTaxRateModal(false);
      setSelectedTaxRate(null);
      await loadTaxConfigurationData();
    } catch (err) {
      console.error('Error saving tax rate:', err);
      setError(err?.message);
    }
  };

  const handleDeleteTaxRate = async (taxRateId) => {
    if (!window.confirm('Are you sure you want to delete this tax rate?')) return;
    
    try {
      await TaxConfigurationService?.deleteTaxRate(taxRateId);
      await loadTaxConfigurationData();
    } catch (err) {
      console.error('Error deleting tax rate:', err);
      setError(err?.message);
    }
  };

  const tabs = [
    { id: 'tax-rates', label: 'Tax Rates', icon: '📊' },
    { id: 'jurisdictions', label: 'Jurisdictions', icon: '🗺️' },
    { id: 'compliance', label: 'Compliance', icon: '✅' },
    { id: 'ai-assistant', label: 'AI Assistant', icon: '🤖' }
  ];

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Authentication Required</h2>
          <p className="text-gray-600">Please log in to access tax configuration.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="py-6">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-3xl font-bold text-gray-900">Tax Configuration</h1>
                <p className="mt-1 text-sm text-gray-500">
                  Manage tax rates, jurisdictions, and compliance settings for your organization
                </p>
              </div>
              
              {activeTab === 'tax-rates' && (
                <div className="flex space-x-3">
                  <button
                    onClick={handleCreateTaxRate}
                    className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors duration-200 flex items-center space-x-2"
                  >
                    <span>➕</span>
                    <span>Add Tax Rate</span>
                  </button>
                  
                  <button
                    onClick={loadTaxConfigurationData}
                    className="bg-gray-100 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-200 transition-colors duration-200 flex items-center space-x-2"
                  >
                    <span>🔄</span>
                    <span>Refresh</span>
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      {/* Navigation Tabs */}
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="flex space-x-8" aria-label="Tabs">
            {tabs?.map((tab) => (
              <button
                key={tab?.id}
                onClick={() => setActiveTab(tab?.id)}
                className={`py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap flex items-center space-x-2 ${
                  activeTab === tab?.id
                    ? 'border-blue-500 text-blue-600' :'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <span>{tab?.icon}</span>
                <span>{tab?.label}</span>
              </button>
            ))}
          </nav>
        </div>
      </div>
      {/* Content Area */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-md p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <span className="text-red-400">❌</span>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">Error</h3>
                <p className="mt-1 text-sm text-red-700">{error}</p>
              </div>
            </div>
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="flex items-center space-x-2">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
              <span className="text-gray-600">Loading tax configuration...</span>
            </div>
          </div>
        ) : (
          <>
            {activeTab === 'tax-rates' && (
              <TaxRatesTable
                taxRates={taxRates}
                jurisdictions={jurisdictions}
                onEdit={handleEditTaxRate}
                onDelete={handleDeleteTaxRate}
                loading={loading}
              />
            )}

            {activeTab === 'jurisdictions' && (
              <JurisdictionManager
                jurisdictions={jurisdictions}
                organizationId={organization?.id}
                onUpdate={loadTaxConfigurationData}
              />
            )}

            {activeTab === 'compliance' && (
              <ComplianceTracker
                complianceRules={complianceRules}
                jurisdictions={jurisdictions}
                organizationId={organization?.id}
                onUpdate={loadTaxConfigurationData}
              />
            )}

            {activeTab === 'ai-assistant' && (
              <AITaxAssistant
                taxRates={taxRates}
                jurisdictions={jurisdictions}
                organizationId={organization?.id}
              />
            )}
          </>
        )}
      </div>
      {/* Tax Rate Form Modal */}
      {showTaxRateModal && (
        <TaxRateFormModal
          taxRate={selectedTaxRate}
          jurisdictions={jurisdictions}
          onSubmit={handleTaxRateSubmit}
          onClose={() => {
            setShowTaxRateModal(false);
            setSelectedTaxRate(null);
          }}
        />
      )}
    </div>
  );
};

export default TaxConfiguration;